// Centralised handler for "your admin session was superseded by a login on
// another device" errors returned by `admin-auth-validate` / `admin-data-api`.
//
// Without this, when single-session enforcement kicks the user out on origin A
// (e.g. hibane.ai/admin) because they signed in on origin B (e.g. the Lovable
// preview), origin A keeps its now-invalid token in localStorage forever and
// every dashboard / social-media query silently returns empty — looking like
// a data bug. We instead clear the token, surface a one-time toast, and send
// the user to the login screen so they can reauthenticate cleanly.
import { toast } from "sonner";
import { clearAdminSessionStore } from "@/lib/adminSessionStore";

let alreadyHandled = false;

export function handleSupersededAdminSession(): void {
  if (alreadyHandled) return;
  alreadyHandled = true;

  try {
    clearAdminSessionStore(["admin_user", "device_fingerprint_validated"]);
  } catch {
    /* storage may be unavailable */
  }

  try {
    toast.error("You signed in on another device", {
      description: "Please sign in again here to continue.",
      duration: 8000,
    });
  } catch {
    /* toast container may not be mounted yet */
  }

  // Preserve where the user was so they land back on the same tab after login.
  const here = window.location.pathname + window.location.search;
  const redirect = encodeURIComponent(here);
  // Use a hard navigation to wipe in-memory query caches that still hold
  // failed responses tagged to the dead session.
  window.location.replace(`/admin/login?redirect=${redirect}&reason=session_superseded`);
}

/**
 * Inspect an arbitrary error/payload and trigger recovery if it carries the
 * `session_superseded` reason from our edge functions. Safe to call with any
 * shape — returns true if recovery was triggered so callers can short-circuit.
 */
export function maybeHandleSupersededFromPayload(payload: unknown): boolean {
  if (!payload || typeof payload !== "object") return false;
  const obj = payload as Record<string, unknown>;
  const reason =
    (obj.reason as string | undefined) ??
    (typeof obj.code === "string" && obj.code === "SESSION_SUPERSEDED" ? "session_superseded" : undefined);
  if (reason === "session_superseded") {
    handleSupersededAdminSession();
    return true;
  }
  return false;
}
